import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '../../context/ThemeContext';

function Sparkline({ values, dark }) {
    const w = 90;
    const h = 26;
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);

    const pts = values.map((v, i) => {
        const x = (i / (values.length - 1)) * w;
        const t = (v - min) / (max - min || 1);
        const y = h - t * h;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    });

    const strokeColor = dark ? "rgba(255,255,255,0.8)" : "rgba(239, 68, 68, 0.8)";

    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ opacity: 0.9 }}>
            <polyline
                points={pts.join(" ")}
                fill="none"
                stroke={strokeColor}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
        </svg>
    );
}

const GlassTooltip = ({ active, payload, label, dark }) => {
    if (!active || !payload?.length) return null;

    const v = payload[0].value;
    const sparkValues = [Math.max(0, v - 1), v, Math.max(0, v - 2), v, Math.min(v + 1, v * 1.2), v];

    return (
        <div style={{
            padding: '16px 20px',
            minWidth: '220px',
            backgroundColor: dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: dark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '16px',
            boxShadow: '0 18px 70px rgba(0,0,0,0.35)',
            color: dark ? '#fff' : '#0f172a'
        }}>
            <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '16px' }}>
                <div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>{label}</div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>Saatlik detay</div>
                </div>
                <div style={{ marginTop: '4px' }}>
                    <Sparkline values={sparkValues} dark={dark} />
                </div>
            </div>

            <div style={{
                margin: '12px 0',
                height: '1px',
                width: '100%',
                backgroundColor: dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'
            }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', opacity: 0.8 }}>
                <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: dark ? '#fff' : '#0f172a',
                    boxShadow: dark ? '0 0 14px rgba(255,255,255,0.85)' : '0 0 14px rgba(0,0,0,0.25)'
                }} />
                <span style={{ fontSize: '0.875rem' }}>Araç Sayısı:</span>
                <span style={{ fontSize: '0.875rem', fontWeight: '700', marginLeft: 'auto' }}>{v}</span>
            </div>
        </div>
    );
};

const HourlyTrafficChart = ({ data }) => {
    const { theme } = useTheme();
    const dark = theme !== 'light';

    const isEmpty = !data || data.length === 0;

    const gridStroke = dark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)";
    const axisStroke = dark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.25)";
    const xTick = dark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.55)";
    const yTick = dark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)";

    return (
        <div style={{
            width: '100%',
            height: 350,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '24px',
            border: dark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            backgroundColor: dark ? 'rgba(3, 3, 3, 0.4)' : 'rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(20px)',
            boxShadow: dark ? '0 30px 90px rgba(0,0,0,0.65)' : '0 30px 90px rgba(0,0,0,0.12)',
            padding: '24px'
        }}>
            {/* Noise texture */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay"
                style={{
                    backgroundImage:
                        "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"160\" height=\"160\"><filter id=\"n\"><feTurbulence type=\"fractalNoise\" baseFrequency=\"0.9\" numOctaves=\"2\" stitchTiles=\"stitch\"/></filter><rect width=\"160\" height=\"160\" filter=\"url(%23n)\" opacity=\"0.45\"/></svg>')",
                }}
            />

            {/* Glow blobs */}
            <div className="pointer-events-none absolute inset-0">
                <div style={{ position: 'absolute', top: '-10%', left: '-10%', width: '250px', height: '250px', borderRadius: '50%', background: dark ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.05)', filter: 'blur(80px)' }} />
                <div style={{ position: 'absolute', bottom: '-10%', right: '-10%', width: '250px', height: '250px', borderRadius: '50%', background: dark ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.03)', filter: 'blur(80px)' }} />
            </div>

            <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: dark ? '#fff' : '#0f172a', margin: 0 }}>
                        Saatlik Yoğunluk
                    </h3>
                </div>

                <div style={{ width: '100%', height: 260 }}>
                    {isEmpty ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-secondary italic opacity-60">Veri Yok</div>
                        </div>
                    ) : (
                        <ResponsiveContainer>
                            <LineChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="premiumLineGrad" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor={dark ? "rgba(255,255,255,0.25)" : "rgba(239, 68, 68, 0.2)"} />
                                        <stop offset="50%" stopColor={dark ? "rgba(255,255,255,0.95)" : "rgba(239, 68, 68, 0.9)"} />
                                        <stop offset="100%" stopColor={dark ? "rgba(255,255,255,0.25)" : "rgba(239, 68, 68, 0.2)"} />
                                    </linearGradient>

                                    <filter id="glowEffect" x="-50%" y="-50%" width="200%" height="200%">
                                        <feGaussianBlur stdDeviation="4" result="blur" />
                                        <feMerge>
                                            <feMergeNode in="blur" />
                                            <feMergeNode in="SourceGraphic" />
                                        </feMerge>
                                    </filter>
                                </defs>

                                <CartesianGrid strokeDasharray="4 6" vertical={false} stroke={gridStroke} />

                                <XAxis
                                    dataKey="name"
                                    tickLine={false}
                                    axisLine={{ stroke: axisStroke, strokeWidth: 1.5 }}
                                    tick={{ fill: xTick, fontSize: 12, fontWeight: 500 }}
                                    interval={3}
                                />

                                <YAxis
                                    tickLine={false}
                                    axisLine={{ stroke: axisStroke, strokeWidth: 1.5 }}
                                    tick={{ fill: yTick, fontSize: 12, fontWeight: 500 }}
                                    allowDecimals={false}
                                />

                                <Tooltip content={<GlassTooltip dark={dark} />} cursor={false} />

                                <Line
                                    type="monotone"
                                    dataKey="count"
                                    stroke={dark ? "rgba(255,255,255,0.18)" : "rgba(239, 68, 68, 0.12)"}
                                    strokeWidth={10}
                                    dot={false}
                                    activeDot={false}
                                    isAnimationActive={false}
                                />

                                <Line
                                    type="monotone"
                                    dataKey="count"
                                    stroke="url(#premiumLineGrad)"
                                    strokeWidth={3}
                                    filter="url(#glowEffect)"
                                    dot={{
                                        r: 4,
                                        fill: dark ? "rgba(255,255,255,0.95)" : "rgba(239, 68, 68, 0.9)",
                                        stroke: "rgba(0,0,0,0)",
                                    }}
                                    activeDot={{
                                        r: 7,
                                        fill: dark ? "#fff" : "#ef4444",
                                        stroke: dark ? "rgba(255,255,255,0.3)" : "rgba(239, 68, 68, 0.2)",
                                        strokeWidth: 10,
                                    }}
                                    isAnimationActive={true}
                                    animationDuration={1500}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HourlyTrafficChart;
